<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="true"
          :showAddNew="true"
          :btnTitle="'Import'"
          @onClickAddNewButton="startImport"
          @onClickCloseButton="goToList"
          title="Review Fixed Asset Import"
      />
    </div>

    <div class="p-2">
      <template v-for="(rows, batchKey) in csvData" :key="batchKey">
        <div v-if="!completedBatches.includes(batchKey)" class="mb-3">
          <div class="d-flex align-items-center mb-2">
            <h5 class="mb-0">Batch: {{ batchKey }}</h5>
            <span class="badge bg-light-secondary ms-2">{{ rows.length }} item(s)</span>
            <div v-if="batchKey === importingBatch" class="spinner-border spinner-border-sm text-success ms-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-bordered table-hover align-middle mb-0">
              <thead>
              <tr>
                <th style="width: 40px;"></th>
                <th>Asset Name</th>
                <th>Asset Class</th>
                <th>Qty</th>
                <th>Custodian</th>
                <th>Supplier</th>
                <th>Capitalization Date</th>
                <th>Depreciation Rate</th>
              </tr>
              </thead>
              <tbody>
              <ImportAssetRow
                  v-for="(row, index) in rows"
                  :key="index"
                  :row="row"
                  :cost-centres="costCentres"
                  :businesses="businesses"
                  @onClickUpdate="updateRow"
              />
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>

    <GlobalLoader/>
  </div>
</template>

<script setup>
import {ref, onMounted, inject} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import TitleButton from '@/components/atom/TitleButton.vue'
import GlobalLoader from '@/components/atom/GlobalLoader.vue'
import ImportAssetRow from '@/components/molecule/asset-register-import/ImportAssetRow.vue'
import handleAssetRegister from '@/services/modules/assetRegister/assetRegister'
import handleCostCentres from '@/services/modules/procurement/organization-settings/costCentre'
import handleCBusinesses from '@/services/modules/businesses'

const route = useRoute()
const router = useRouter()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const {
  fetchAssetRegisterImportCsvData,
  finalUploadAssetRegisterBatch,
  updateAssetRegisterImportRow
} = handleAssetRegister()
const {fetchCostCentreList} = handleCostCentres()
const {fetchBusinessList} = handleCBusinesses()

const csvData = ref({})
const importingBatch = ref('')
const isImporting = ref(false)
const completedBatches = ref([])
const costCentres = ref([])
const businesses = ref([])

function getQuery() {
  return `?id=${route.query.rowId}&company_id=${route.params.companyId}`
}

function goToList() {
  router.push({name: 'import-fixed-asset', params: route.params, query: route.query})
}

function loadCsvData() {
  fetchAssetRegisterImportCsvData(getQuery()).then(res => {
    if (!res.status) {
      showError(res.message)
      return
    }
    csvData.value = res.data
  })
}

function updateRow(row) {
  const payload = {
    company_id: route.params.companyId,
    id: route.query.rowId,
    row_key: row.data.row_key,
    row: row.data
  }

  updateAssetRegisterImportRow(payload).then(res => {
    if (!res.status) {
      showError(res.message)
      return
    }
    showSuccess(res.message)
    row.is_edit = false
    loadCsvData()
  })
}

function startImport() {
  if (isImporting.value) return

  isImporting.value = true
  const batchKeys = Object.keys(csvData.value)
  importBatch(batchKeys, 0)
}

function importBatch(batchKeys, index) {
  if (index === batchKeys.length) {
    isImporting.value = false
    router.back()
    return
  }

  const batchKey = batchKeys[index]
  const query = `?company_id=${route.params.companyId}&id=${route.query.rowId}&batch=${batchKey}`
  importingBatch.value = batchKey

  finalUploadAssetRegisterBatch(query).then(res => {
    if (!res.status) {
      showError(res.message)
      importingBatch.value = ''
      isImporting.value = false
      return
    }

    if (res.has_errors) {
      showError(res.message)
      importingBatch.value = ''
      isImporting.value = false
      loadCsvData()
      return
    }

    completedBatches.value.push(batchKey)
    importingBatch.value = ''
    importBatch(batchKeys, index + 1)
  }).catch(() => {
    showError('Something went wrong')
    importingBatch.value = ''
    isImporting.value = false
  })
}

onMounted(() => {
  loadCsvData()

  const query = `?company_id=${route.params.companyId}`
  fetchCostCentreList(query).then(res => {
    if (res.data) costCentres.value = res.data
  })
  fetchBusinessList(query).then(res => {
    if (res.data) businesses.value = res.data
  })
})
</script>
